/**
 * Протокол WebSocket между Mini App и игровым сервером.
 *
 * Принципы:
 *  - клиент шлёт НАМЕРЕНИЕ, сервер отвечает НОВЫМ ПРЕДСТАВЛЕНИЕМ;
 *  - клиент никогда не вычисляет исход и не двигает часы авторитетно;
 *  - каждый ход несёт `ply` — номер полухода. Повтор или устаревший ход
 *    сервер молча отбрасывает, поэтому переотправка при обрыве безопасна.
 */
import type { ClockSnapshot } from "./clock.js";
import type { GameResult, PlayerId } from "./index.js";

export type GameKind = "chess" | "durak" | "checkers" | "reversi" | "gomoku" | "seabattle";

export type MatchStatus = "pending" | "active" | "finished" | "aborted";

/** Снимок партии для конкретного игрока: представление движка + часы + мета. */
export interface MatchSnapshot<V = unknown> {
  matchId: string;
  game: GameKind;
  status: MatchStatus;
  timeControl: string;
  /** Хэш сида — публикуется сразу, позволяет проверить честность раздачи. */
  seedCommitment: string;
  /** Сид раскрывается только когда партия окончена. */
  seed: string | null;
  /** То, что игроку разрешено видеть. Формат зависит от игры. */
  view: V;
  clock: ClockSnapshot | null;
  players: {
    me: MatchPlayer;
    opponent: MatchPlayer | null;
  };
  /** Порядковый номер следующего ожидаемого полухода. */
  ply: number;
  result: GameResult | null;
  /** Кто предложил ничью и ждёт ответа. */
  drawOfferBy: PlayerId | null;
  /**
   * Изменение рейтинга после партии. Появляется ПОЗЖЕ финального снимка:
   * пересчёт идёт в базу асинхронно, поэтому сервер досылает обновление.
   */
  ratingDelta: Record<PlayerId, { before: number; after: number; delta: number }> | null;
}

export interface MatchPlayer {
  id: PlayerId;
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  online: boolean;
}

// ─────────────────────────── клиент → сервер ───────────────────────────

export type ClientMessage =
  /** Обязательно первым сообщением. Неавторизованный сокет закрывается. */
  | { t: "auth"; token: string }
  | { t: "join"; matchId: string }
  | { t: "leave"; matchId: string }
  | { t: "move"; matchId: string; ply: number; move: unknown }
  | { t: "resign"; matchId: string }
  | { t: "draw:offer"; matchId: string }
  | { t: "draw:accept"; matchId: string }
  | { t: "draw:decline"; matchId: string }
  | { t: "chat"; matchId: string; text: string }
  | { t: "reaction"; matchId: string; emoji: string }
  /** Замер задержки: сервер отвечает `pong` тем же `id`. */
  | { t: "ping"; id: number };

// ─────────────────────────── общение в партии ───────────────────────────

/**
 * Реакции — закрытый список.
 *
 * Произвольные эмодзи не принимаем намеренно: со случайными соперниками
 * (когда появится матчмейкинг) свободный ввод превращается в канал
 * оскорблений, а закрытый список остаётся безобидным.
 */
export const REACTIONS = ["👍", "😂", "😮", "😡", "🔥", "🤝", "🤔", "😭"] as const;
export type Reaction = (typeof REACTIONS)[number];

export function isReaction(value: string): value is Reaction {
  return (REACTIONS as readonly string[]).includes(value);
}

export const CHAT_MAX_LENGTH = 200;
/** Не больше стольких сообщений за окно — защита от спама в лицо сопернику. */
export const CHAT_RATE = { windowMs: 10_000, max: 5 } as const;
export const REACTION_RATE = { windowMs: 10_000, max: 8 } as const;
/** Сколько последних сообщений держим в комнате для переподключившегося. */
export const CHAT_HISTORY = 50;

export interface ChatMessage {
  id: number;
  by: PlayerId;
  text: string;
  at: number;
}

// ─────────────────────────── сервер → клиент ───────────────────────────

export type ServerMessage =
  | { t: "auth:ok"; userId: PlayerId; serverTime: number }
  | { t: "match"; snapshot: MatchSnapshot }
  /** Прислали, когда партия завершилась — снимок уже содержит result и раскрытый сид. */
  | { t: "match:end"; snapshot: MatchSnapshot }
  | { t: "opponent"; online: boolean }
  | { t: "draw:offered"; by: PlayerId }
  | { t: "draw:declined" }
  | { t: "chat"; message: ChatMessage }
  /** Накопленная переписка — приходит при входе в комнату. */
  | { t: "chat:history"; messages: ChatMessage[] }
  | { t: "reaction"; by: PlayerId; emoji: Reaction; at: number }
  | { t: "pong"; id: number; serverTime: number }
  | { t: "error"; code: string; message: string };

export const WS_CLOSE = {
  /** Не прислал auth вовремя. */
  AUTH_TIMEOUT: 4001,
  /** Токен недействителен. */
  UNAUTHORIZED: 4002,
  /** Слишком много сообщений. */
  RATE_LIMITED: 4003,
  /** Подключение с того же аккаунта вытеснило это. */
  SUPERSEDED: 4004,
} as const;

/** Сколько ждём сообщение `auth` после установки соединения. */
export const AUTH_TIMEOUT_MS = 5_000;
/** Интервал heartbeat: сервер шлёт ping, мёртвые сокеты закрываются. */
export const HEARTBEAT_INTERVAL_MS = 30_000;
